import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Download } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { SelectField, TextField } from '@/components/forms/Field';
import { Spinner } from '@/components/common/Spinner';
import { hrReportsApi, payrollApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { formatDate } from '@/utils/format';
import { BarRow, Days, Money, PayrollStatusBadge, Stat } from './shared';

/**
 * HR and payroll reports.
 *
 * <p>Everything here aggregates what was recorded rather than recalculating it.
 * A report on a run from three years ago shows what that run actually paid, not
 * what today's salary structure would pay for the same people — the difference
 * between a report and a simulation.
 */
export function HrReportsPage() {
  const { can } = useAuth();

  const [periodId, setPeriodId] = useState('');
  const [from, setFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [to, setTo] = useState(`${new Date().getFullYear()}-12-31`);

  const periods = useQuery({
    queryKey: queryKeys.payroll.list({ page: 0, size: 24 }),
    queryFn: () => payrollApi.list({ page: 0, size: 24 }),
    enabled: can(PERMISSIONS.PAYROLL_PERIOD_VIEW),
  });

  const summary = useQuery({
    queryKey: queryKeys.hr.payrollSummary(periodId),
    queryFn: () => hrReportsApi.payrollSummary(periodId),
    enabled: periodId !== '',
  });

  const leave = useQuery({
    queryKey: queryKeys.hr.leaveSummary({ from, to }),
    queryFn: () => hrReportsApi.leaveSummary(from, to),
    enabled: can(PERMISSIONS.LEAVE_REQUEST_VIEW),
  });

  const maxDepartment = Math.max(1, ...(summary.data?.byDepartment ?? []).map((row) => row.net));
  const maxLeave = Math.max(1, ...(leave.data?.byType ?? []).map((row) => row.days));

  return (
    <div>
      <PageHeader
        title="HR Reports"
        description="What a payroll run cost, and how much leave the school took."
        icon={<ClipboardList className="h-5 w-5" aria-hidden="true" />}
      />

      {can(PERMISSIONS.PAYROLL_PERIOD_VIEW) && (
        <Card className="p-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="w-full max-w-xs">
              <SelectField
                label="Payroll run"
                value={periodId}
                onChange={(event) => setPeriodId(event.target.value)}
              >
                <option value="">Choose a run…</option>
                {(periods.data?.data ?? []).map((period) => (
                  <option key={period.id} value={period.id}>
                    {period.name} ({period.status.toLowerCase()})
                  </option>
                ))}
              </SelectField>
            </div>
            {periodId !== '' &&
              can(PERMISSIONS.PAYSLIP_VIEW) &&
              (summary.data?.status === 'APPROVED' || summary.data?.status === 'PAID') && (
                <Button
                  variant="secondary"
                  onClick={() => void hrReportsApi.payrollRegisterCsv(periodId)}
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                  Bank transfer list
                </Button>
              )}
          </div>

          {periodId === '' ? (
            <p className="mt-4 text-[13px] text-muted">
              Choose a run to see what it cost, by department and by component.
            </p>
          ) : summary.isPending ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : summary.data !== undefined ? (
            <>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                  {summary.data.periodName}
                </h2>
                <PayrollStatusBadge status={summary.data.status} />
                <span className="text-[12px] text-muted">
                  {formatDate(summary.data.periodStart)} – {formatDate(summary.data.periodEnd)}
                </span>
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <Stat label="Payslips" value={summary.data.employeeCount} />
                <Stat label="Gross" value={<Money value={summary.data.totalGross} />} />
                <Stat label="Deductions" value={<Money value={summary.data.totalDeductions} />} />
                <Stat
                  label="Net paid"
                  value={<Money value={summary.data.totalNet} />}
                  tone="success"
                />
              </div>

              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <section>
                  <h3 className="text-[11px] font-extrabold uppercase tracking-[0.06em] text-muted">
                    Cost by department
                  </h3>
                  <div className="mt-1">
                    {summary.data.byDepartment.map((row) => (
                      <BarRow
                        key={row.department}
                        label={`${row.department} (${row.employees})`}
                        value={row.net}
                        max={maxDepartment}
                        suffix={<Money value={row.net} />}
                      />
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-[11px] font-extrabold uppercase tracking-[0.06em] text-muted">
                    By component
                  </h3>
                  <table className="mt-1 w-full text-sm">
                    <tbody>
                      {summary.data.byComponent.map((row) => (
                        <tr key={`${row.code}-${row.lineType}`} className="border-b border-line">
                          <td className="py-1.5 text-ink">{row.name}</td>
                          <td className="py-1.5 text-right text-[12px] text-muted">
                            {row.lineType === 'EARNING' ? 'earning' : 'deduction'}
                          </td>
                          <td className="py-1.5 text-right">
                            <Money value={row.total} />
                          </td>
                          <td className="py-1.5 pl-3 text-right text-[12px] text-subtle">
                            ×{row.payslips}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              </div>
            </>
          ) : null}
        </Card>
      )}

      {can(PERMISSIONS.LEAVE_REQUEST_VIEW) && (
        <Card className="mt-3 p-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap gap-3">
              <div className="w-40">
                <TextField
                  label="Leave taken from"
                  type="date"
                  value={from}
                  onChange={(event) => setFrom(event.target.value)}
                />
              </div>
              <div className="w-40">
                <TextField
                  label="To"
                  type="date"
                  min={from}
                  value={to}
                  onChange={(event) => setTo(event.target.value)}
                />
              </div>
            </div>
            <Button variant="secondary" onClick={() => void hrReportsApi.leaveSummaryCsv(from, to)}>
              <Download className="h-4 w-4" aria-hidden="true" />
              Export
            </Button>
          </div>

          {leave.isPending ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : leave.data !== undefined ? (
            <>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Stat
                  label="Days taken"
                  value={<Days value={leave.data.totalDays} />}
                  hint="Approved and taken leave only"
                />
                <Stat
                  label="Of which unpaid"
                  value={<Days value={leave.data.unpaidDays} />}
                  tone={leave.data.unpaidDays > 0 ? 'warning' : 'neutral'}
                  hint="What payroll deducts against"
                />
              </div>

              {leave.data.byType.length === 0 ? (
                <p className="mt-3 text-[13px] text-muted">No leave was taken in this range.</p>
              ) : (
                <div className="mt-3">
                  {leave.data.byType.map((row) => (
                    <BarRow
                      key={row.leaveTypeId}
                      label={`${row.leaveTypeName}${row.paid ? '' : ' (unpaid)'} · ${row.requests} request${row.requests === 1 ? '' : 's'}`}
                      value={row.days}
                      max={maxLeave}
                      suffix={<Days value={row.days} />}
                    />
                  ))}
                </div>
              )}
            </>
          ) : null}
        </Card>
      )}
    </div>
  );
}
